import React from 'react';
import {Form, List} from 'semantic-ui-react';
import {find, isEqual} from 'lodash';
import {i18n} from '../helpers/localization';

/**
 * Wrapper for Semantic Form Field.
 */
export default class FormInput extends React.Component {
    constructor(props) {
        super(props);

        /**
         * Type of form input ('checkbox', 'input', 'textarea', etc.)
         *
         * @type {string|boolean}
         */
        this.controlType = props.control?.name ?? false;

        /**
         * Unique ID for every field so we can focus on it in case of errors.
         *
         * @type {string}
         */
        this.id = props.id ?? btoa(`${this.props?.name}${Date.now()}${props?.label}${Math.random()}`);

        let initialValue = props.value;
        if (typeof initialValue === 'string' && initialValue.indexOf(i18n.Selected) !== -1) {
            initialValue = i18n.All;
        }

        this.state = {
            initialValue,
            internalValue: props.value,
            clearable: typeof props?.onClear === 'function',
        };
    }

    /**
     * Check if we should re-render the component.
     *
     * @param {object} nextProps
     * @param {object} nextState
     * @param {object} snapshot
     * @returns {boolean}
     */
    shouldComponentUpdate(nextProps, nextState, snapshot) {
        return nextProps.value !== this.props.value
            || nextProps.disabled !== this.props.disabled
            || nextProps.checked !== this.props.checked
            || nextState.internalValue !== this.state.internalValue
            || this.props.name == null
            || !isEqual(this.props.errors, nextProps.errors)
            || !isEqual(this.props.options, nextProps.options);
    }

    /**
     * Set focus to an element which has errors.
     */
    componentDidUpdate(prevProps, prevState, snapshot) {
        if (this.props.value !== prevProps.value && this.props.value !== this.state.internalValue) {
            this.setState({internalValue: this.props.value});
        }

        if (!isEqual(this.props.errors, prevProps.errors) && find(this.props.errors, {propertyName: this.props.name})) {
            try {
                document.getElementById(this.id)?.scrollIntoView({behavior: 'smooth', block: 'center'});
            } catch (e) {
                console.error(e);
            }
        }
    }

    render() {
        let {errors, children, placeholder, validation, clearable, onClear, emptyValue, ...rest} = this.props;

        emptyValue = emptyValue ?? this.state.initialValue;
        errors = errors ?? [];

        let usePlaceholder = placeholder;
        if (!usePlaceholder && typeof (this.props.label) === 'string') {
            usePlaceholder = this.props.label;
        }

        /** @type {boolean|string} */
        let isValid = true;
        if (typeof validation === 'function') {
            isValid = validation(this.props.value);

            if (
                isValid === false
                || (
                    isValid?.length > 0
                    && !find(errors, {
                        propertyName: this.props.name,
                        errorMessage: isValid,
                    })
                )
            ) {
                errors.push({ propertyName: this.props.name, errorMessage: isValid });
            }
        }

        const errorMessage = getErrorList(errors, this.props.name);
        let error = errorMessage ? { content: errorMessage } : false;

        // Semantic doesn't support errors if field has children,
        // we must remove the error message and just show an errored field (red bg).
        // also dont show error msg popup if validation returns boolean: false
        if ((error && children?.length > 0) || (error && isValid === false)) {
            error = true;
        }
        
        const fluid =
            this.controlType !== 'TextArea'
            && this.controlType !== 'Radio'
            && this.controlType !== 'Checkbox'
            && this.controlType !== false ? true : null;

        if (this.controlType === 'Select') {
            rest = {
                noResultsMessage: rest.loading ? i18n.Searching : i18n.NoResults,
                ...rest,
            };

            if (clearable === undefined || clearable === null) {
                rest.clearable = !this.props.required;
            }
        }

        if (this.state.clearable) {
            rest.onChange = (event, data) => {
                this.setState({internalValue: data.value});

                if (typeof this.props.onChange === 'function') {
                    this.props.onChange(event, data);
                }
            }

            if (this.state.internalValue && this.state.internalValue !== emptyValue) {
                rest.icon = {
                    name: 'times',
                    className: 'link dropdown',
                    onClick: () => {
                        this.setState({internalValue: emptyValue});

                        onClear({name: rest.name, value: emptyValue});
                    },
                };
            }
        }

        if (rest.type === 'number') {
            rest.max = 999999999999;
        }

        return <>
            <Form.Field
                id={this.id}
                fluid={fluid}
                error={error}
                placeholder={usePlaceholder}
                {...rest}
            >
                {children}
            </Form.Field>
        </>;
    }
}

/**
 * Get validation errors in a List component.
 *
 * @param {Object[]} errors Array of errors.
 * @param {String|String[]} propertyName Name (or names) of property the errors are for.
 * @returns {boolean|React.Component} False if no errors are found for the property, List of errors otherwise.
 */
export function getErrorList(errors, propertyName) {
    let errorMessage = false;

    if (errors && errors.length > 0) {
        var errorMessages;
        if (Array.isArray(propertyName)) {
            errorMessages = errors
                .filter(error => propertyName.indexOf(error.propertyName) !== -1)
                .map((error, i) => <List.Item key={i}>{error.errorMessage}</List.Item>);
        } else {
            errorMessages = errors
                .filter(error => error.propertyName === propertyName)
                .map((error, i) => <List.Item key={i}>{error.errorMessage}</List.Item>);
        }

        if (errorMessages.length > 0) {
            errorMessage = <List>{errorMessages}</List>;
        }
    }

    return errorMessage;
}
